import { Injectable } from '@angular/core'
import { addDoc, collection, collectionData, deleteDoc, doc, docData, Firestore, getDoc, getDocs, limit, orderBy, query, runTransaction, serverTimestamp, setDoc, updateDoc, where } from '@angular/fire/firestore'
import { Client, clientsPath, FirestoreStructure, LogSearch, logSearchesPath, Occupant, occupantsPath, paymentMethodsPath } from '@parkupp/core'
import { BehaviorSubject, firstValueFrom, map, Observable, Subject, Subscription } from 'rxjs'
import { DocumentData } from 'rxfire/firestore/interfaces'
import * as firestore from 'firebase/firestore'
import { Auth } from '@angular/fire/auth'
import { AuthenticationService } from './authentication.service'
import { Router } from '@angular/router'
import { AdmytService } from './admyt.service'
import { CampaignTrackingService } from './campaign-tracking.service'
import { GlobalService } from './global.service'
import { PageViewService } from './page-view.service'
import { AffiliateService } from './affiliate.service'

@Injectable({
    providedIn: 'root',
})
export class ClientService {
    collection = collection(this.firestore, FirestoreStructure.CLIENTS)

    activeClient: Client | null
    clientSubscription: Subscription
    activeClientReady = new Subject()

    private activeClientSubject = new BehaviorSubject<Client | null>(null)
    activeClient$: Observable<Client | null> = this.activeClientSubject.asObservable()

    constructor(
        public firestore: Firestore,
        public auth: Auth,
        private authenticationSvc: AuthenticationService,
        private router: Router,
        private admytService: AdmytService,
        private campaignTrackingService: CampaignTrackingService,
        private globalService: GlobalService,
        private pageViewService: PageViewService,
        private affiliateService: AffiliateService
    ) {
        this.auth.onAuthStateChanged(async (user) => {
            this.activeClient = null
            this.activeClientSubject.next(null)
            if (user?.uid && !user.isAnonymous) {
                await this.initializeClientWithCampaignData(user.uid, user.metadata.creationTime)
                this.affiliateService.claimReferralIfReferred()
                this.getActiveClient(user?.uid!)
            }
        })
    }

    getDocRef(clientKey: string) {
        return doc(this.firestore, `${FirestoreStructure.CLIENTS}/${clientKey}`)
    }

    private async updateLastLoginIfNeeded(uid: string) {
        try {
            const clientDoc = await getDoc(this.getDocRef(uid))
            if (clientDoc.exists()) {
                const clientData = clientDoc.data()
                const lastLogin = clientData['lastLogin']

                // Only update if last login was more than 24 hours ago (or never set)
                const now = new Date()
                const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)

                if (!lastLogin || lastLogin.toDate() < twentyFourHoursAgo) {
                    this.updateClient({ lastLogin: serverTimestamp() as firestore.Timestamp }, uid)
                }
            } else {
                // Client document doesn't exist yet - this is normal for new users
                // Don't try to update lastLogin until they complete their profile
                console.log(`ℹ️ Client document doesn't exist yet for UID: ${uid} - skipping lastLogin update`)
            }
        } catch (error) {
            console.error('Error checking lastLogin:', error)
            // Don't fallback to updating if document doesn't exist
        }
    }

    adminGetEmailFromClientKey(clientId: string): Observable<any | undefined> {
        const clientPrivateDocRef = doc(this.firestore, `${FirestoreStructure.ADMIN_CLIENTS}/${clientId}`)
        return docData(clientPrivateDocRef).pipe(
            map((data: any) => {
                return { email: data.email, phoneNumber: data.phoneNumber }
            }) // Assuming 'email' is stored in the private client data
        )
    }

    list(): Observable<Client[]> {
        const clientsQuery = query(this.collection)
        return collectionData(clientsQuery, { idField: '$key' }) as Observable<Client[]>
    }

    get(clientId: string): Observable<any> {
        const clientDocRef = doc(this.firestore, `${FirestoreStructure.CLIENTS}/${clientId}`)
        return docData(clientDocRef, { idField: '$key' })
    }

    async getPromise(clientId: string): Promise<Client> {
        const clientDocRef = doc(this.firestore, `${FirestoreStructure.CLIENTS}/${clientId}`)
        const documentData = await firstValueFrom(docData(clientDocRef, { idField: '$key' }))
        return new Client(documentData)
    }

    getFromEmail(clientId: string): Observable<any> {
        const clientDocRef = doc(this.firestore, `${FirestoreStructure.CLIENTS}/${clientId}`)
        return docData(clientDocRef, { idField: '$key' })
    }

    async getActiveClient(uid: string) {
        if (this.clientSubscription) {
            this.clientSubscription.unsubscribe()
        }
        const documentRef = doc(this.firestore, clientsPath(), uid)
        const documentSnap = await getDoc(documentRef)

        if (documentSnap.exists()) {
            this.activeClient = documentSnap.data() as Client
            this.activeClient.$key = uid

            // TODO: PAR-134 Add email to the client object to improve read speed
            this.activeClient.$email = this.authenticationSvc.email as string
            this.activeClient.$phoneNumber = this.authenticationSvc.phoneNumber as string

            this.activeClientReady.next(this.activeClient! as Client)
            // TODO: This seems to work better
            this.activeClientSubject.next(this.activeClient! as Client)
        } else {
            this.activeClient = null
            this.activeClientSubject.next(null)
        }
    }

    createClient(client: Client, uid: string): Promise<any> {
        client.createdAt = serverTimestamp() as firestore.Timestamp
        client.updatedAt = serverTimestamp() as firestore.Timestamp
        const reference = doc(this.firestore, clientsPath(), uid)
        // Merge: the doc may already hold signup attribution written by initializeClientWithCampaignData
        return setDoc(reference, client.parse(), { merge: true })
    }

    updateClient(fields: { [id: string]: any }, uid: string): Promise<any> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        const reference = doc(this.firestore, clientsPath(), uid)
        return updateDoc(reference, fields)
    }

    /**
     * Capture campaign attribution onto the Client document once, at signup.
     * Creates the doc if missing; if something else created it first (e.g. the profile form),
     * fills in only the attribution fields that are absent. Runs at most once per client
     * (attributionCapturedAt), and only for accounts created in the last day, so a campaign
     * link clicked by a long-standing user never rewrites their history.
     * Also tracks which enterprise the user first registered from.
     */
    async initializeClientWithCampaignData(uid: string, authCreationTime?: string): Promise<void> {
        try {
            if (this.auth.currentUser?.uid !== uid) return
            const clientRef = doc(this.firestore, clientsPath(), uid)
            const campaignData = this.campaignTrackingService.getCampaignData()
            const clickMs = Date.parse(campaignData.googleClickCapturedAt || '')
            if (Number.isFinite(clickMs)) {
                await runTransaction(this.firestore, async transaction => {
                    const snapshot = await transaction.get(clientRef)
                    const current = snapshot.data()
                    const previousValue = current?.['googleClickCapturedAt'] || current?.['gclidCapturedAt']
                    const previous = previousValue?.toMillis?.() || (previousValue?.seconds ? previousValue.seconds * 1000 : Date.parse(previousValue) || 0)
                    if (clickMs > previous) transaction.set(clientRef, {
                        gclid: campaignData.gclid || null, gbraid: campaignData.gbraid || null, wbraid: campaignData.wbraid || null,
                        googleClickCapturedAt: firestore.Timestamp.fromMillis(clickMs),
                        gclidCapturedAt: firestore.Timestamp.fromMillis(clickMs), updatedAt: serverTimestamp(),
                        ...(!snapshot.exists() ? { createdAt: serverTimestamp() } : {}),
                    }, { merge: true })
                })
            }
            const clientSnap = await getDoc(clientRef)
            const existing = clientSnap.exists() ? clientSnap.data() : null
            if (existing?.['attributionCapturedAt']) return

            const createdMs = authCreationTime ? new Date(authCreationTime).getTime() : 0
            const isNewAccount = !createdMs || Date.now() - createdMs < 24 * 60 * 60 * 1000
            if (existing && !isNewAccount) return

            const attribution: Record<string, any> = {
                ...campaignData,
                enterpriseSource: this.globalService.enterpriseKey || 'parkupp',
                visitorId: this.pageViewService.getVisitorId() || null,
            }
            if (Number.isFinite(clickMs)) {
                attribution['googleClickCapturedAt'] = firestore.Timestamp.fromMillis(clickMs)
                attribution['gclidCapturedAt'] = firestore.Timestamp.fromMillis(clickMs)
            }

            const writeData: Record<string, any> = { attributionCapturedAt: serverTimestamp(), updatedAt: serverTimestamp() }
            // Source + campaign are a pair: never mix a stored source with a new campaign
            const hasStoredSourcePair = !!(existing?.['p_source'] || existing?.['p_campaign'])
            for (const [field, value] of Object.entries(attribution)) {
                if (value === undefined || value === null) continue
                if ((field === 'p_source' || field === 'p_campaign') && hasStoredSourcePair) continue
                if (existing?.[field]) continue
                writeData[field] = value
            }
            if (!existing?.['createdAt']) writeData['createdAt'] = serverTimestamp()

            await setDoc(clientRef, writeData, { merge: true })
        } catch (error) {
            console.error('[Client Service] Error initializing client with campaign data:', error)
        }
    }

    subscribeToClient(uid: string): Observable<DocumentData> {
        return docData(doc(this.firestore, clientsPath(), uid))
    }

    requiresProfileCompletion(client: Client | null | undefined): boolean {
        return !client?.firstName
    }

    getAllClientDataOnce(uid: string): Promise<Client> {
        return new Promise<Client>((resolve) => {
            const clientSubscription = docData(doc(this.firestore, clientsPath(), uid)).subscribe((clientDocument: any) => {
                clientSubscription.unsubscribe()

                const client: Client = clientDocument

                const promises = []
                promises.push(
                    new Promise<void>((resolve) => {
                        collectionData(collection(this.firestore, paymentMethodsPath(uid), uid)).subscribe((paymentMethodData: any[]) => {
                            client.$paymentMethods = paymentMethodData
                        })
                    })
                )

                Promise.all(promises).then(() => {
                    resolve(client)
                })
            })
        })
    }

    logClientSearch(googlePlace: LogSearch) {
        googlePlace.createdAt = serverTimestamp() as firestore.Timestamp
        addDoc(collection(this.firestore, logSearchesPath()), googlePlace.parse())
    }

    async deleteOccupant(occupantKey: string, clientKey: string) {
        try {
            // First get the occupant data to retrieve registration info
            const occupantRef = doc(this.firestore, occupantsPath(clientKey), occupantKey)
            const occupantSnap = await getDoc(occupantRef)

            if (occupantSnap.exists()) {
                const occupantData = occupantSnap.data() as Occupant

                // Check if this client is an Admyt user
                const client = await this.getPromise(clientKey)
                if (this.isAdmytUser(client) && occupantData.registration) {
                    console.log('[ClientService] Removing vehicle from Admyt:', occupantData.registration)
                    try {
                        // Remove the vehicle from Admyt system
                        const admytUserId = this.getAdmytTenantUserId(client)
                        if (admytUserId) {
                            await this.admytService.deleteUserCar(admytUserId, occupantData.registration)
                            console.log('[ClientService] Successfully removed vehicle from Admyt')
                        }
                    } catch (admytError) {
                        console.error('[ClientService] Failed to remove vehicle from Admyt:', admytError)
                        // Continue with ParkUpp deletion even if Admyt fails
                    }
                }
            }

            // Mark the occupant as deleted in ParkUpp
            const fieldsToUpdate = {
                deletedAt: serverTimestamp() as firestore.Timestamp,
            }
            await updateDoc(occupantRef, fieldsToUpdate)

            console.log('[ClientService] Vehicle deleted successfully')
            return
        } catch (error) {
            console.error('[ClientService] Error deleting occupant:', error)
            throw error
        }
    }

    /**
     * Automatically add a vehicle to Admyt access control if conditions are met
     * @param occupantKey - The key of the occupant/vehicle
     * @param clientKey - The key of the client
     * @returns Promise that resolves when the operation is complete
     */
    async autoAddToAdmytAccessControl(occupantKey: string, clientKey: string): Promise<void> {
        try {
            console.log('[ClientService] Checking if vehicle should be auto-added to Admyt access control')

            // Get the client to check if they are an Admyt user
            const client = await this.getPromise(clientKey)
            if (!this.isAdmytUser(client)) {
                console.log('[ClientService] Client is not an Admyt user, skipping auto-add')
                return
            }

            // Get the occupant data
            const occupantRef = doc(this.firestore, occupantsPath(clientKey), occupantKey)
            const occupantSnap = await getDoc(occupantRef)

            if (!occupantSnap.exists()) {
                console.log('[ClientService] Occupant not found, skipping auto-add')
                return
            }

            const occupantData = occupantSnap.data() as Occupant

            // Check if vehicle has registration number
            if (!occupantData.registration) {
                console.log('[ClientService] Vehicle has no registration number, skipping auto-add')
                return
            }

            // Get all occupants to check current LPR count
            const occupants = await this.getOccupantsPromise(clientKey)
            const lptCount = occupants.filter((occupant: Occupant) => occupant.admytLpr).length
            const lptLimit = 2

            if (lptCount >= lptLimit) {
                console.log(`[ClientService] Already have ${lptCount} vehicles with LPR access control (limit: ${lptLimit}), skipping auto-add`)
                return
            }

            // Add vehicle to Admyt access control
            console.log('[ClientService] Auto-adding vehicle to Admyt access control:', occupantData.registration)

            const admytUserId = this.getAdmytTenantUserId(client)
            if (!admytUserId) {
                console.log('[ClientService] No Admyt tenant user ID found, skipping auto-add')
                return
            }

            const carData = {
                plate: occupantData.registration,
                make: occupantData.make || occupantData.model || 'unknown',
                model: occupantData.model || 'unknown',
            }

            await this.admytService.createUserCar(admytUserId, carData)
            await this.updateOccupantFields(clientKey, occupantKey, { admytLpr: true })

            console.log('[ClientService] Successfully auto-added vehicle to Admyt access control')
        } catch (error: any) {
            // Extract specific error message from response if available for better logging
            let errorMessage = 'Unknown error occurred'
            if (error?.responseData?.error) {
                errorMessage = error.responseData.error
            } else if (error?.message) {
                errorMessage = error.message
            }

            console.error('[ClientService] Error during auto-add to Admyt access control:', errorMessage, error)
            // Don't throw the error - we don't want to fail vehicle creation if Admyt fails
        }
    }

    async getClientByUid(uid: string): Promise<Client | null> {
        const clientDocRef = doc(this.firestore, `${FirestoreStructure.CLIENTS}/${uid}`)
        const clientSnap = await getDoc(clientDocRef)

        if (clientSnap.exists()) {
            const clientData = clientSnap.data() as Client
            clientData.$key = uid
            return new Client(clientData, uid)
        } else {
            return null
        }
    }

    deletePaymentMethod(clientKey: string, paymentMethodKey: string) {
        return deleteDoc(doc(this.firestore, paymentMethodsPath(clientKey), paymentMethodKey))
    }

    getOccupants(clientId: string): Observable<Occupant[]> {
        return collectionData(collection(this.firestore, occupantsPath(clientId)), { idField: '$key' }).pipe(
            map((occupants) => occupants.map((data) => new Occupant(data)).filter((occupant) => !occupant.deletedAt))
        )
    }

    /**
     * Get occupants as a Promise instead of Observable
     * @param clientKey - The client key
     * @returns Promise resolving to array of occupants
     */
    async getOccupantsPromise(clientKey: string): Promise<Occupant[]> {
        return firstValueFrom(this.getOccupants(clientKey))
    }

    async updateOccupantFields(clientId: string, occupantKey: string, fields: any) {
        const occupantRef = doc(this.firestore, occupantsPath(clientId), occupantKey)
        await updateDoc(occupantRef, fields)
    }

    /**
     * Check if a user has Admyt access control features.
     * Checks both the legacy admytTenantUserId and the denormalized hasAdmytAccess flag.
     */
    isAdmytUser(client?: Client | null): boolean {
        if (!client) {
            client = this.activeClient
        }
        return !!(client && (client.admytTenantUserId || client.hasAdmytAccess))
    }

    /**
     * Get the active client's Admyt Tenant User ID
     * @param client - The client object to check
     * @returns string or null
     */
    getAdmytTenantUserId(client?: Client | null): string | null {
        if (!client) {
            client = this.activeClient
        }
        return client?.admytTenantUserId || null
    }

    /**
     * Check if the current active client is an Admyt user
     * @returns boolean indicating if the active client is an Admyt user
     */
    isActiveClientAdmytUser(): boolean {
        return this.isAdmytUser(this.activeClient)
    }

    /**
     * Update client fields with Admyt integration
     * @param fields - Fields to update
     * @param uid - User ID
     */
    async updateFields(fields: { [id: string]: any }, uid: string): Promise<any> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        const reference = doc(this.firestore, clientsPath(), uid)
        return updateDoc(reference, fields)
    }
}
